import { useApolloClient, useLazyQuery } from '@apollo/client';
import type { JobDetails } from '@seek/ca-graphql-schema/types';
import { useEffect } from 'react';
import { useIsSplitViewEnabled } from 'src/store/featureFlags/hooks';
import useShouldFetchJobDetails from 'src/handlers/useShouldFetchJobDetails';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { GET_JOB_DETAILS_WITH_PERSONALISED } from 'src/modules/graphql/queries/jobDetails';
import type { GetJobDetailsWithPersonalised } from 'src/modules/graphql/queries/types/GetJobDetailsWithPersonalised';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectCountry,
  selectFeatureFlag,
  selectLanguage,
  selectSessionId,
  selectZone,
} from 'src/store/selectors';
import { v4 as uuid } from 'uuid';
import { useSelectedLocale } from './useSelectedLocale';
import { useTimezone } from './useTimezone';

export const useFetchJobDetails = (jobId = '') => {
  const analyticsFacade = useAnalyticsFacade();
  const sessionId = useSelector(selectSessionId) ?? '';
  const zone = useSelector(selectZone);
  const countryCode = useSelector(selectCountry);
  const locale = useSelectedLocale();
  const authenticated = useSelector(selectAuthenticated);
  const apolloClient = useApolloClient();
  const isSplitViewEnabled = useIsSplitViewEnabled();
  const isApolloJDVEnabled = useSelector(
    selectFeatureFlag('isApolloJDVEnabled'),
  );
  const timezone = useTimezone();
  const languageCode = useSelector(selectLanguage);
  const shouldFetchJobDetails = useShouldFetchJobDetails(jobId);

  const [getJobDetails, { loading, error, refetch }] =
    useLazyQuery<GetJobDetailsWithPersonalised>(
      GET_JOB_DETAILS_WITH_PERSONALISED,
      {
        fetchPolicy: 'network-only',
        onCompleted: (getJobDetailsResponse) => {
          // Fix me: the JobDetails type from @seek/ca-graphql-schema/types
          // and GetJobDetailsWithPersonalised type are essentially the same
          // but jobDetailsFetched method only accepts JobDetails type
          const jobDetails =
            getJobDetailsResponse?.jobDetails as JobDetails | null;

          if (jobDetails) {
            analyticsFacade.jobDetailsFetched(jobDetails);
          }
        },
      },
    );

  useEffect(() => {
    // Fix me: (as it's currently designed) this hook should not need to know
    // about whether it's in splitview mode or not
    if (
      isApolloJDVEnabled &&
      isSplitViewEnabled &&
      jobId &&
      shouldFetchJobDetails
    ) {
      getJobDetails({
        variables: {
          jobDetailsViewedCorrelationId: uuid(),
          jobId,
          locale,
          sessionId,
          zone,
          languageCode,
          countryCode,
        },
      });
    }
  }, [
    getJobDetails,
    isSplitViewEnabled,
    jobId,
    zone,
    locale,
    authenticated,
    sessionId,
    isApolloJDVEnabled,
    languageCode,
    countryCode,
    shouldFetchJobDetails,
  ]);

  const currentJobDetails = apolloClient.readQuery({
    query: GET_JOB_DETAILS_WITH_PERSONALISED,
    variables: {
      jobId,
      locale,
      sessionId,
      zone,
      languageCode,
      countryCode,
      timezone,
    },
  });

  return {
    currentJobDetails: currentJobDetails?.jobDetails,
    isLoading: loading,
    isError: Boolean(error),
    refetchJobDetails: refetch!,
  };
};
